import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { element } from 'protractor';

@Injectable()
export class Http2Service {
    constructor(private readonly httpClient: HttpClient) {
    }

    private readonly tQuery = 'query';
    private readonly tMutation = 'mutation';
    private rootUrl: string;    

    get url() {
        return this.rootUrl;
    }

    /**
     * Perform any request type
     */
    request<T>(url: string, config: IHttp2RequestConfig): Promise<T> {
        if (config.body) {
            config.body = JSON.stringify(config.body);
        }
        config = {
            headers: {
                'Content-Type': 'application/json'
            },
            params: {},
            ...config
        };

        // let token = this.tokenStorage.getToken();
        // if (token) {
        //     config.headers = Object.assign({}, config.headers, {
        //         'X-Auth-Token': token
        //     });

        //     //Include the token as a querystring parameter. This is only for debugging purposes
        //     if (this.appSettings.includeTokenInQuerystring) {
        //         config.params.AuthToken = token;
        //     }
        // }

        url = this.rootUrl ? this.rootUrl + url : url;
        if (url.indexOf('?') > 0) {
            url = url + '&';
        } else {
            url = url + '?';
        }
        // Send pdauthAppName
        url = url + `pdauthAppName=${environment.pdauthAppName}`;
        if (environment.pdauthToken) {
            //Send PDAuth token, used for developing locally 
            url = url + `&token=${environment.pdauthToken}`;
        }

        // remove any trailing slash from the url
        if (url.lastIndexOf('/') === url.length - 1) {
            url = url.substring(0, url.length - 1);
        }

        return this.httpClient.request<T>(config.method, url, config).toPromise().then(result => {
            return result;
        }, errorResponse => {
            return Promise.reject(errorResponse.error);
        });
    }
    /**
     * Perform a GET graphql query
     * @param query GraphQL query
     * @param variables variables to be inserted into query
     * @param method "GET" | "POST" | "PUT" | "DELETE"
     * @param dataPath retrieves the data at the specified named path.
     */
    async graphqlRequest<T = any>(query: string, variables: any, method: Method = 'GET', dataPath: string = null) {
        let queryName: string;
        const type = query.trim().toLowerCase().startsWith(this.tQuery) ? this.tQuery : this.tMutation;
        if (type === this.tQuery) {
            // find the query name from the query and use that in the querystring to help with debugging to distinguish this request
            queryName = query.substring(query.indexOf(this.tQuery) + this.tQuery.length, query.indexOf('{')).trim();
        } else {
            queryName = query.substring(query.indexOf(this.tMutation) + this.tMutation.length, query.indexOf('(')).trim();
        }
        // remove any excess whitespace from the query only for GET requests since this is passed on the URL
        if (method === 'GET') {
            query = this.graphqlTrim(query);
        }
        let response: { data: any };

        try {
            response = await this.request<{ data: any }>(`graphql?${queryName}`, {
                body: method !== 'GET' ? {
                    query,
                    variables
                } : undefined,
                method,
                params: method === 'GET' ? {
                    query,
                    variables: JSON.stringify(variables)
                } : undefined
            });
        } catch (e) {
            if (e && !e.message && e.errors && e.errors[0]) {
                e.message = e.errors[0].message;
            }
            throw e;
        }
        if (!dataPath) {
            return <T>response.data;
        } else {
            const parts = dataPath.split('.');
            let result = response.data;
            for (const part of parts) {
                result = result[part];
            }
            return <T>result;
        }
    }
    private graphqlTrim(text: string) {
        text = text.replace(/\s+/g, ' ');
        return text;
    }

    get<T>(url: string, params?: any, config?: any): Promise<T> {
        const cfg = {
            ...config,
            method: 'GET',
            params
        };

        return this.request<T>(url, cfg);
    }

    post<T>(url: string, body?: any, config?: any): Promise<T> {
        const cfg = {
            body,
            method: 'POST',
            ...config
        };
        return this.request<T>(url, cfg);
    }

    put<T>(url: string, body?: any, config?: any): Promise<T> {
        const cfg = {
            body,
            method: 'PUT',
            ...config
        };

        return this.request<T>(url, cfg);
    }

    delete<T>(url: string, body?: any, config?: any): Promise<T> {
        const cfg = {
            body,
            method: 'DELETE',
            ...config
        };
        return this.request<T>(url, cfg);
    }
}

export interface IHttp2RequestConfig {
    body?: any;
    headers?: { [key: string]: any };
    method?: Method;
    params?: { [key: string]: any };
}
export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';
